// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * @file avatarResolver.js
 * @description Resolución canónica ÚNICA de URL de avatar (sesión, contactos, navbar).
 *
 * AVATAR_RESOLVER_LOCK — No duplicar esta prioridad en otros módulos; importar pickCanonicalAvatarSourceUrl / resolveAvatar.
 * Prioridad obligatoria:
 *   1. profile_avatar_url
 *   2. avatar_url
 *   3. avatar
 *   4. foto_perfil
 *   5. profile_avatar_full_url (compat API legado; después de 1–4 para no pisar URL “principal” más reciente)
 *   6. fallback (DEFAULT_AVATAR en getAvatarUrl / resolveAvatar)
 *
 * Display con basePath: construirUrlImagen(pickCanonicalAvatarSourceUrl(user) || DEFAULT_AVATAR, false).
 */

const DEFAULT_AVATAR = '/assets/default-avatar.png';

/**
 * Placeholder inline (data URI) si el archivo en disco y default-asset no cargan.
 * Evita cadenas onError infinitas y dependencias de red. 2026-04-24T180000Z
 */
export const AVATAR_INLINE_PLACEHOLDER_SVG =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="64" height="64" role="img" aria-hidden="true"><rect width="64" height="64" fill="%23cbd5e1"/><circle cx="32" cy="22" r="10" fill="%2364748b"/><path d="M8 60c3-12 12-20 24-20s21 8 24 20" fill="%2364748b"/></svg>'
  );

function maybeResolveBackendAvatarOrigin(url) {
  if (!url || typeof url !== 'string') return url;
  if (typeof window === 'undefined') return url;
  const normalized = url.trim();
  if (
    !normalized.startsWith('/app/user-images/') &&
    !normalized.startsWith('/user-images/')
  ) {
    return normalized;
  }
  const host = String(window.location?.hostname ?? '').toLowerCase();
  const port = String(window.location?.port ?? '');
  const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '::1';
  const isDevPort = port !== '' && port !== '80' && port !== '443';
  if (!isLocal || !isDevPort) return normalized;
  // En dev server (3000/3001/...) los /app/user-images no existen.
  // Forzamos origen backend servido (localhost sin puerto) para evitar 404 de avatar.
  return `${window.location.protocol}//${window.location.hostname}${normalized}`;
}

/**
 * @param {object|null|undefined} user
 * @returns {string|null} URL sin aplicar basePath
 */
export function pickCanonicalAvatarSourceUrl(user) {
  if (!user || typeof user !== 'object') return null;
  const v =
    user.profile_avatar_url ??
    user.avatar_url ??
    user.avatar ??
    user.foto_perfil ??
    user.profile_avatar_full_url ??
    null;
  if (v == null) return null;
  const s = String(v).trim();
  return s !== '' ? maybeResolveBackendAvatarOrigin(s) : null;
}

export function getAvatarUrl(user) {
  return pickCanonicalAvatarSourceUrl(user) || DEFAULT_AVATAR;
}

/**
 * Resuelve la URL de avatar con cache busting por avatar_version (sin duplicar v=).
 * @param {Object} user - Usuario o contacto
 * @returns {string} URL de avatar lista para <img src>
 */
export function resolveAvatar(user) {
  return getAvatarUrl(user);
}

/** Misma prioridad que pickCanonicalAvatarSourceUrl; alias explícito para Navbar/perfil. */
export function resolveAuthenticatedUserAvatarUrl(user) {
  return pickCanonicalAvatarSourceUrl(user);
}
